import React, { useEffect, useState, Suspense } from "react";
import {
  Box,
  Button,
  Input,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  IconButton,
  Stack,
  Spinner,
  useToast,
  Image,
  Skeleton,
  SkeletonText,
  // ChakraLink,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import Sidebar from "../Sidebar";
import Navbar from "../Navbar/Navbar";
import { Link as RouterLink } from "react-router-dom";
import { Link as ChakraLink } from "react-router-dom";
import axios from "axios";
import * as mod from "../../url"; // update with your URL file path

const token = JSON.parse(sessionStorage.getItem("superAdminLawvs"))?.data
  ?.token;
const config = { headers: { Authorization: token } };

const SIDEBAR_WIDTH = "250px";
const RECORDS_PER_PAGE = 20;

const AllArticles = () => {
  const [allArticles, setArticles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const toast = useToast();

  //  Fetch articles from API
  const fetchAllArticles = async () => {
    try {
      const { data } = await axios.get(
        `${mod.api_url}/api/v1/article/get_all_articles`
      );
      setArticles(data.Articles);
    } catch (error) {
      console.error("Error fetching:", error);
      toast({
        title: "Failed to fetch articles.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllArticles();
  }, []);

  //  Toggle article status
  const handleStatusToggle = async (slug, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      await axios.put(
        `${mod.api_url}/api/v1/article/update_article_status/${slug}`,
        { status: newStatus },
        config
      );

      setArticles((prev) =>
        prev.map((a) => (a.slug === slug ? { ...a, status: newStatus } : a))
      );
    } catch (error) {
      console.error("Failed to update status", error);
    }
  };

  //  Delete article
  const handleDelete = async (slug) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this article?"
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(
        `${mod.api_url}/api/v1/article/delete_article/${slug}`,
        config
      );

      setArticles((prev) => prev.filter((a) => a.slug !== slug));
      toast({
        title: "Article deleted successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Failed to delete article.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  //  Filter logic (case-insensitive)
  const filteredArticles = allArticles.filter((a) =>
    a?.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  //  Pagination
  const totalPages = Math.ceil(filteredArticles.length / RECORDS_PER_PAGE);
  const startIdx = (currentPage - 1) * RECORDS_PER_PAGE;
  const currentRecords = filteredArticles.slice(
    startIdx,
    startIdx + RECORDS_PER_PAGE
  );

  return (
    <>
      <Navbar />
      <Sidebar />

      <Box mt="70px" ml={{ base: 0, md: SIDEBAR_WIDTH }} p={3}>
        <Text fontSize="2xl" mb={4} bg="#D29B3F" p={3}>
          <strong>All Articles</strong>
        </Text>

        {/* 🔍 Search + Add New */}
        <Flex
          bg="white"
          p={4}
          mb={4}
          borderRadius="md"
          boxShadow="sm"
          align="center"
          flexWrap="wrap"
          gap={4}
        >
          <Input
            placeholder="Search by title or keyword..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            maxW="300px"
          />

          <Flex ml="auto">
            <Button
              colorScheme="blue"
              leftIcon={<AddIcon />}
              as={RouterLink}
              to="/create-article"
              _hover={{ bg: "blue.100" }}
            >
              Add New Record
            </Button>
          </Flex>
        </Flex>

        <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
          <Text fontSize="sm" mb={2} textAlign="right">
            Total Records: {filteredArticles.length}
          </Text>

          {/* 🧩 Suspense with Skeleton fallback */}
          <Suspense
            fallback={
              <Stack spacing={4}>
                {[...Array(5)].map((_, i) => (
                  <Box key={i} p={4} borderWidth="1px" borderRadius="md">
                    <Skeleton height="20px" width="30%" mb={2} />
                    <SkeletonText noOfLines={2} spacing="3" />
                  </Box>
                ))}
              </Stack>
            }
          >
            {loading ? (
              <Flex justify="center" align="center" py={10}>
                <Spinner size="xl" />
              </Flex>
            ) : (
              <>
                <Table
                  variant="simple"
                  size="sm"
                  style={{
                    border: "1px solid #ccc",
                    borderCollapse: "collapse",
                  }}
                >
                  <Thead bg="gray.100">
                    <Tr>
                      <Th border="1px solid #ccc">ID</Th>
                      <Th border="1px solid #ccc">Image</Th>
                      <Th border="1px solid #ccc">Title</Th>
                      <Th border="1px solid #ccc">Posted on</Th>
                      <Th border="1px solid #ccc">Edit</Th>
                      <Th border="1px solid #ccc">Status</Th>
                      <Th border="1px solid #ccc">Delete</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {currentRecords.map((article, index) => (
                      <Tr key={article._id}>
                        <Td border="1px solid #ccc">{startIdx + index + 1}</Td>
                        <Td border="1px solid #ccc">
                          <Image
                            src={article?.articleImage}
                            alt={article?.title}
                            width="120px"
                            height="80px"
                            // objectFit="cover"
                            borderRadius="md"
                          />
                        </Td>
                        <Td border="1px solid #ccc">
                          <ChakraLink
                            as={RouterLink}
                            // to={`/single-article/${article.slug}`}
                            color="blue.500"
                            _hover={{ textDecoration: "none" }}
                          >
                            {article?.title.slice(0, 80)}
                          </ChakraLink>
                        </Td>
                        <Td border="1px solid #ccc">
                          {new Date(article.postedOn).toLocaleDateString()}
                        </Td>
                        <Td border="1px solid #ccc">
                          <ChakraLink
                            as={RouterLink}
                            to={`/update-article/${article.slug}`}
                          >
                            <IconButton
                              icon={<EditIcon />}
                              colorScheme="blue"
                              size="sm"
                            />
                          </ChakraLink>
                        </Td>
                        <Td border="1px solid #ccc">
                          <Button
                            size="sm"
                            colorScheme={
                              article.status === "active" ? "green" : "red"
                            }
                            onClick={() =>
                              handleStatusToggle(article.slug, article.status)
                            }
                          >
                            {article.status === "active"
                              ? "Active"
                              : "Inactive"}
                          </Button>
                        </Td>
                        <Td border="1px solid #ccc">
                          <IconButton
                            icon={<DeleteIcon />}
                            colorScheme="red"
                            size="sm"
                            onClick={() => handleDelete(article.slug)}
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>

                {/* 🔢 Pagination */}
                <Flex justify="space-between" align="center" mt={4}>
                  <Stack direction="row" spacing={2}>
                    {Array.from({ length: totalPages }).map((_, idx) => (
                      <Button
                        key={idx}
                        size="sm"
                        onClick={() => setCurrentPage(idx + 1)}
                        colorScheme={currentPage === idx + 1 ? "blue" : "gray"}
                        variant={currentPage === idx + 1 ? "solid" : "outline"}
                      >
                        {idx + 1}
                      </Button>
                    ))}
                  </Stack>
                </Flex>
              </>
            )}
          </Suspense>
        </Box>
      </Box>
    </>
  );
};

export default AllArticles;
